import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Close as CloseIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Keyboard as KeyboardIcon,
  Send as SendIcon,
} from '@mui/icons-material'
import { Scanner, IDetectedBarcode } from '@yudiel/react-qr-scanner'
import { socialApi } from '@api/endpoints/social'
import { rewardsApi } from '@api/endpoints/rewards'
import type { ValidateRewardCodeResponse } from '@api/types/rewards'
import { RedeemPreviewDialog } from '@features/rewards/components/RedeemPreviewDialog'
import { useAuthStore } from '@store/authStore'
import { adventureKeys } from '@features/adventures/hooks/useAdventures'
import { parseApprovalQrPayload } from '@features/adventures/utils/moderatorApproval'
import ModeratorApprovalScanDialog from '@features/adventures/components/player/nodes/ModeratorApprovalScanDialog'
import { parseFriendQrPayload } from '@features/social/utils/friendQr'
import FriendRequestScanDialog from '@features/social/components/FriendRequestScanDialog'

interface ScanCodeDialogProps {
  open: boolean
  onClose: () => void
}

// Extract code from URL - handles URLs like https://example.com/?CODE or plain codes
const extractCodeFromUrl = (rawValue: string): string => {
  try {
    // Check if it's a URL
    if (rawValue.includes('://') || rawValue.startsWith('www.')) {
      const url = new URL(rawValue.startsWith('www.') ? `https://${rawValue}` : rawValue)
      // Get everything after the ? (the search string without the leading ?)
      if (url.search) {
        return url.search.substring(1) // Remove the leading ?
      }
      // Fallback: check pathname for code
      const pathParts = url.pathname.split('/').filter(Boolean)
      if (pathParts.length > 0) {
        return pathParts[pathParts.length - 1]
      }
    }
  } catch {
    // Not a valid URL, treat as plain code
  }
  return rawValue
}

export function ScanCodeDialog({ open, onClose }: ScanCodeDialogProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const [tabIndex, setTabIndex] = useState(0)
  const [manualCode, setManualCode] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const isProcessingRef = useRef(false)

  // Reward redemption state
  const [rewardValidation, setRewardValidation] = useState<ValidateRewardCodeResponse | null>(null)
  const [showRedeemDialog, setShowRedeemDialog] = useState(false)
  const [currentRewardCode, setCurrentRewardCode] = useState<string | null>(null)
  const [isRedeeming, setIsRedeeming] = useState(false)

  // Moderator-approval scan state
  const [approvalRequestId, setApprovalRequestId] = useState<string | null>(null)
  // Friend-request scan state
  const [friendRequestUserId, setFriendRequestUserId] = useState<string | null>(null)

  const handleClose = () => {
    setManualCode('')
    setError(null)
    setCameraError(null)
    isProcessingRef.current = false
    setIsProcessing(false)
    setTabIndex(0)
    // Reset reward state
    setRewardValidation(null)
    setShowRedeemDialog(false)
    setCurrentRewardCode(null)
    setIsRedeeming(false)
    setApprovalRequestId(null)
    setFriendRequestUserId(null)
    onClose()
  }

  const processCode = async (code: string) => {
    if (!code.trim()) return

    // Set ref immediately to block any subsequent rapid-fire scans
    isProcessingRef.current = true
    setIsProcessing(true)
    setError(null)

    try {
      // All codes go through the unified sharecode endpoint
      const data = await socialApi.processShareCode(code.trim())

      if (!data) {
        setError('No response from server.')
        isProcessingRef.current = false
        setIsProcessing(false)
        return
      }

      // Handle different response actions
      switch (data.action) {
        case 'AuthenticationRequired':
          // User needs to log in - redirect to login with return URL
          handleClose()
          // For reward codes, redirect to login then back to scan
          if (data.codeType === 'Reward') {
            navigate('/login', {
              state: {
                returnUrl: `/code/${code}`,
                message: data.message,
              }
            })
          } else {
            // For user codes, pass the full code as referral code
            const referralCodeToPass = data.codeType === 'User' || code.toUpperCase().startsWith('U')
              ? code
              : data.creatorReferralCode
            navigate('/login', {
              state: {
                returnUrl: data.adventureId ? `/adventures/${data.adventureId}` : `/code/${code}`,
                message: data.message,
                referralCode: referralCodeToPass
              }
            })
          }
          break

        case 'RewardCodeValid':
          // Valid reward code - show redeem dialog
          if (data.rewardInfo) {
            // Convert rewardInfo to ValidateRewardCodeResponse format
            const validation: ValidateRewardCodeResponse = {
              valid: true,
              canRedeem: data.canRedeem ?? true,
              message: data.message || null,
              instanceId: data.rewardInfo.instanceId,
              title: data.rewardInfo.templateName,
              owner: null,
              ownerDisplayName: data.rewardInfo.ownerDisplayName || null,
              remainingUses: data.rewardInfo.remainingUses,
              isExpired: data.rewardInfo.isExpired ?? false,
              canOverrideExpiration: data.rewardInfo.canOverrideExpiration ?? false,
            }
            setRewardValidation(validation)
            setCurrentRewardCode(code)
            setShowRedeemDialog(true)
            setIsProcessing(false)
            // Don't reset isProcessingRef here - will be reset when dialog closes
            return
          } else {
            setError('Reward information not available.')
            isProcessingRef.current = false
            setIsProcessing(false)
          }
          break

        case 'InvalidCode':
          setError(data.message || 'Invalid code.')
          isProcessingRef.current = false
          setIsProcessing(false)
          break

        case 'GeocacheComplete':
          // Geocache completed successfully
          handleClose()
          navigate(`/adventures/${data.adventureId}`, {
            state: {
              geocacheComplete: true,
              justCompleted: true,
              message: data.message || `${data.adventureName} completed!`
            }
          })
          break

        case 'AlreadyCompleted':
          // Already completed this adventure
          setError(data.message || 'You have already completed this adventure.')
          isProcessingRef.current = false
          setIsProcessing(false)
          break

        case 'JoinCriteriaFulfilled':
          // Join criteria now met - invalidate the adventure cache so it refetches with updated eligibility
          if (data.adventureId) {
            // Invalidate the specific adventure detail query to trigger a refetch
            queryClient.invalidateQueries({ queryKey: adventureKeys.detail(data.adventureId) })
          }
          handleClose()
          navigate(`/adventures/${data.adventureId}`, {
            state: {
              fromShareCode: true,
              joinCriteriaFulfilled: true,
              message: data.message || 'You can now join this adventure!'
            }
          })
          break

        case 'RequirementFulfilled':
          // Workflow requirement fulfilled (in-adventure scanning)
          handleClose()
          navigate(`/adventures/${data.adventureId}`, {
            state: {
              requirementFulfilled: true,
              locationId: data.locationId,
              requirementId: data.requirementId,
              message: data.message || 'Requirement fulfilled!'
            }
          })
          break

        case 'WrongLocation':
          // Valid code but wrong location
          setError(data.message || 'This code is valid but not for your current location.')
          isProcessingRef.current = false
          setIsProcessing(false)
          break

        case 'DiscoveryOnly':
          // General discovery - redirect to target
          handleClose()
          if (data.redirectUrl) {
            navigate(data.redirectUrl, {
              state: { fromShareCode: true, message: data.message }
            })
          } else if (data.targetType === 'Adventure' && data.targetId) {
            navigate(`/adventures/${data.targetId}`, {
              state: { fromShareCode: true, message: data.message }
            })
          } else if (data.targetType === 'Group' && data.targetId) {
            navigate(`/groups/${data.targetId}`, {
              state: { fromShareCode: true, message: data.message }
            })
          } else if ((data.targetType === 'User' || data.targetType === 'Referral') && data.targetId) {
            // User code - navigate to profile if authenticated, otherwise register
            if (isAuthenticated) {
              navigate(`/profile/${data.targetId}`, {
                state: { fromShareCode: true, message: data.message }
              })
            } else {
              navigate(`/register?code=${encodeURIComponent(code)}`, {
                state: { fromShareCode: true, message: data.message }
              })
            }
          } else {
            // Fallback to code page
            navigate(`/code/${code}`, {
              state: { validated: true, response: data }
            })
          }
          break

        default:
          // Handle success/failure without specific action
          if (data.success) {
            handleClose()
            if (data.redirectUrl) {
              navigate(data.redirectUrl, {
                state: { fromShareCode: true, message: data.message }
              })
            } else if (data.adventureId) {
              navigate(`/adventures/${data.adventureId}`, {
                state: { fromShareCode: true, message: data.message }
              })
            }
          } else {
            // Error cases
            if (data.requiresAdditionalCriteria) {
              setError(data.message || 'You do not meet the required criteria.')
            } else {
              setError(data.message || 'Invalid code. Please check and try again.')
            }
            isProcessingRef.current = false
            setIsProcessing(false)
          }
          break
      }
    } catch (err: unknown) {
      console.error('Code validation error:', err)
      // Try to extract error message from response
      const axiosError = err as { response?: { data?: { message?: string } } }
      const errorMessage = axiosError.response?.data?.message || 'Failed to validate code. Please try again.'
      setError(errorMessage)
      isProcessingRef.current = false
      setIsProcessing(false)
    }
  }

  const handleScan = useCallback((detectedCodes: IDetectedBarcode[]) => {
    // Use ref for immediate synchronous check to prevent race conditions
    if (isProcessingRef.current || detectedCodes.length === 0) return

    const rawValue = detectedCodes[0].rawValue
    if (!rawValue) return

    // Moderator-approval QR payloads carry a special prefix — open the
    // Approve/Reject dialog instead of routing through the share-code path.
    const parsedApprovalId = parseApprovalQrPayload(rawValue)
    if (parsedApprovalId) {
      isProcessingRef.current = true
      setApprovalRequestId(parsedApprovalId)
      return
    }

    // Friend-share QR payloads — open the friend-request prompt instead
    // of routing through the share-code path.
    const parsedFriendId = parseFriendQrPayload(rawValue)
    if (parsedFriendId) {
      isProcessingRef.current = true
      setFriendRequestUserId(parsedFriendId)
      return
    }

    // Extract the actual code from the URL
    const code = extractCodeFromUrl(rawValue)
    if (!code) return

    // Set ref IMMEDIATELY after validation, before any async work
    isProcessingRef.current = true
    processCode(code)
  }, [])

  const handleCameraError = useCallback((error: unknown) => {
    console.error('Camera error:', error)
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        setCameraError('Camera access was denied. Please enable camera permissions or use manual entry.')
      } else if (error.name === 'NotFoundError') {
        setCameraError('No camera found. Please use manual entry.')
      } else {
        setCameraError('Unable to access camera. Please use manual entry.')
      }
    } else {
      setCameraError('Unable to access camera. Please use manual entry.')
    }
    setTabIndex(1)
  }, [])

  const handleManualSubmit = () => {
    const trimmed = manualCode.trim()
    if (trimmed) {
      processCode(trimmed)
    }
  }

  // Handle reward redemption
  const handleRewardRedeem = async (overrideExpiration: boolean) => {
    if (!currentRewardCode) return

    setIsRedeeming(true)
    try {
      const result = await rewardsApi.redeemReward({
        uniqueCode: currentRewardCode,
        forceExpiredRedemption: overrideExpiration,
      })

      if (result.success) {
        // Close the dialog and show success
        handleClose()
        // Navigate to rewards page or show success toast
        navigate('/rewards', {
          state: {
            redeemSuccess: true,
            message: result.message || 'Reward redeemed successfully!',
          },
        })
      } else {
        throw new Error(result.message || 'Failed to redeem reward')
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } }; message?: string }
      throw new Error(
        axiosError.response?.data?.message || axiosError.message || 'Failed to redeem reward'
      )
    } finally {
      setIsRedeeming(false)
    }
  }

  const handleRedeemDialogClose = () => {
    setShowRedeemDialog(false)
    setRewardValidation(null)
    setCurrentRewardCode(null)
    isProcessingRef.current = false
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScannerIcon color="primary" />
          <Typography variant="h6">Scan or Enter Code</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Tab selector */}
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          centered
          sx={{ mb: 2 }}
        >
          <Tab icon={<QrCodeScannerIcon />} label="Scan" />
          <Tab icon={<KeyboardIcon />} label="Enter Code" />
        </Tabs>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Scan tab */}
        {tabIndex === 0 && (
          <Box>
            {cameraError ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {cameraError}
              </Alert>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  bgcolor: 'black',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <Scanner
                  onScan={handleScan}
                  onError={handleCameraError}
                  constraints={{
                    facingMode: 'environment',
                  }}
                  sound={false}
                  styles={{
                    container: {
                      width: '100%',
                      height: '100%',
                    },
                    video: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    },
                  }}
                />
                {/* Processing overlay */}
                {isProcessing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      zIndex: 20,
                    }}
                  >
                    <CircularProgress color="inherit" size={48} sx={{ mb: 2 }} />
                    <Typography>Validating code...</Typography>
                  </Box>
                )}
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Point your camera at a QR code to scan it
            </Typography>
          </Box>
        )}

        {/* Manual entry tab */}
        {tabIndex === 1 && (
          <Box>
            <TextField
              fullWidth
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Enter share code or invite code"
              disabled={isProcessing}
              autoFocus
              sx={{ mb: 2 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manualCode.trim()) {
                  handleManualSubmit()
                }
              }}
            />
            <Button
              variant="contained"
              fullWidth
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleManualSubmit}
              disabled={!manualCode.trim() || isProcessing}
            >
              Validate Code
            </Button>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
              Enter a share code, invite code, or geocache code
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Reward Redeem Preview Dialog */}
      <RedeemPreviewDialog
        open={showRedeemDialog}
        onClose={handleRedeemDialogClose}
        validation={rewardValidation}
        onRedeem={handleRewardRedeem}
        isRedeeming={isRedeeming}
      />

      {/* Moderator-approval scan dialog (opens when scanned payload
          matches the approval QR prefix) */}
      <ModeratorApprovalScanDialog
        requestId={approvalRequestId}
        onClose={() => {
          setApprovalRequestId(null)
          isProcessingRef.current = false
        }}
      />
      <FriendRequestScanDialog
        targetUserId={friendRequestUserId}
        onClose={() => {
          setFriendRequestUserId(null)
          isProcessingRef.current = false
        }}
      />
    </Dialog>
  )
}
